'use client'

import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { PlayIcon } from '@phosphor-icons/react'
import { AnimatePresence, motion } from 'framer-motion'

import { VideoPlayer, VideoPlayerRef } from '@/components/common/player'
import { useTaskRatio } from '@/hooks/utils'
import { CreationDetail } from '@/lib/api'
import { useCreationDetail } from '@/lib/api/hooks/creation/use-creation-detail'
import { cn } from '@/lib/utils/cn'

import { UserAvatar } from '../user-avatar'
import { useWorkContext, WorkProvider } from '../work-context'

import { WorkActionSide } from './work-action-side'

export interface WorkVideoCardProps {
  data: CreationDetail
  isActive?: boolean
  className?: string
}

export const WorkVideoCard = memo(
  ({ data: listData, isActive = false, className }: WorkVideoCardProps) => {
    const playerRef = useRef<VideoPlayerRef>(null)
    const cardRef = useRef<HTMLDivElement>(null)
    const [isPaused, setIsPaused] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const [srcReady, setSrcReady] = useState(false)
    const [wantsPlay, setWantsPlay] = useState(false)

    // 从父级 context 获取 setSkipReset，以便传递给子组件
    const { setSkipReset } = useWorkContext()

    // 使用 useCreationDetail 订阅缓存变化，以支持乐观更新
    // listData 作为占位数据，实际数据从缓存中获取
    const { data: apiData } = useCreationDetail(listData.jobId, { initialData: listData })

    // 合并数据：封面和视频地址优先使用列表数据，其他字段使用接口数据
    // 只有当列表数据中没有这些字段时，才使用接口数据
    const data = useMemo(() => {
      const baseData = apiData ?? listData

      return {
        ...baseData,
        // 封面：优先使用列表数据
        thumbnail: listData.thumbnail || baseData.thumbnail,
        // 视频地址：优先使用列表数据
        videoUrl: listData.videoUrl || baseData.videoUrl,
        // cover 是 getter，展开时不会复制，需要手动添加
        cover: baseData.cover
      }
    }, [apiData, listData])

    // 监听视频播放状态
    useEffect(() => {
      const video = cardRef.current?.querySelector('video') as HTMLVideoElement | null
      if (!video) return

      const handlePlay = () => setIsPlaying(true)
      const handlePause = () => setIsPlaying(false)

      video.addEventListener('play', handlePlay)
      video.addEventListener('playing', handlePlay)
      video.addEventListener('pause', handlePause)

      return () => {
        video.removeEventListener('play', handlePlay)
        video.removeEventListener('playing', handlePlay)
        video.removeEventListener('pause', handlePause)
      }
    }, [isActive])

    // 兜底：暂停图标出现时强制停止播放
    useEffect(() => {
      if (isPaused && isPlaying) {
        const video = cardRef.current?.querySelector('video') as HTMLVideoElement | null
        if (video && !video.paused) {
          video.pause()
        }
      }
    }, [isPaused, isPlaying])

    useEffect(() => {
      if (!isActive || !srcReady || !wantsPlay || isPaused) return

      let cancelled = false
      let retryId = 0
      const tryPlay = () => {
        if (cancelled) return
        const video = cardRef.current?.querySelector('video') as HTMLVideoElement | null
        if (!video) {
          retryId = window.setTimeout(tryPlay, 50)
          return
        }
        video.playsInline = true
        video.play()?.catch?.(() => {})
      }
      tryPlay()
      return () => {
        cancelled = true
        if (retryId) window.clearTimeout(retryId)
      }
    }, [isActive, srcReady, wantsPlay, isPaused])

    // 点击：未挂视频则先挂上并播放，已挂则暂停/播放
    const handleVideoClick = () => {
      if (!srcReady) {
        setSrcReady(true)
        setWantsPlay(true)
        setIsPaused(false)
        return
      }
      const video = cardRef.current?.querySelector('video') as HTMLVideoElement | null
      if (video) {
        if (video.paused) {
          video.play()?.catch?.(() => {})
          setIsPaused(false)
          setWantsPlay(true)
        } else {
          video.pause()
          setIsPaused(true)
          setWantsPlay(false)
        }
      } else {
        setWantsPlay(true)
        setIsPaused(false)
      }
    }

    const aspectRatio = useTaskRatio({
      aspectRatio: data.extra.aspectRatioV2,
      orientation: data.extra.aspectRatio
    })

    return (
      <WorkProvider value={{ data, setSkipReset }}>
        <div
          ref={cardRef}
          className={cn('relative flex h-full w-full flex-col bg-primary', className)}
          style={{ touchAction: 'manipulation' }}
        >
          {/* 视频播放器 */}
          <div
            className="relative flex flex-1 items-center justify-center overflow-hidden rounded-b-2xl"
            onClick={handleVideoClick}
          >
            <VideoPlayer
              ref={playerRef}
              aspectRatio={aspectRatio}
              className="flex size-full items-center justify-center pointer-events-none"
              controls={false}
              fit="fill"
              gestures={false}
              load="idle"
              playButton={false}
              src={isActive && srcReady ? data.videoUrl : undefined}
              classNames={{
                video: 'object-contain h-full pointer-events-none !bg-transparent',
                container: 'relative flex-1 items-center justify-center w-full flex pointer-events-none'
              }}
              loading={{
                showOnInitial: true,
                showOnBuffering: true
              }}
              playback={{
                loop: true,
                autoPlay: false,
                muted: false
              }}
              poster={{
                src: data.thumbnail,
                loading: isActive ? 'eager' : 'lazy',
                priority: isActive,
                imageProps: { sizes: '100vw', unoptimized: true }
              }}
            />
            {/* 暂停状态播放图标 - 兜底：播放中强制隐藏 */}
            <AnimatePresence>
              {!isPlaying && (
                <motion.div
                  key="play-icon"
                  animate={{ opacity: 1 }}
                  className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full bg-black/50">
                    <PlayIcon color="white" size={24} weight="fill" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="pointer-events-none absolute bottom-9 z-20 w-full">
              <div className="flex items-end">
                <div className="flex flex-1 flex-col gap-2.5 truncate px-4">
                  <UserAvatar />
                  <div className="flex flex-col truncate">
                    <span className="truncate text-sm font-semibold text-white">
                      {data.nickName}
                    </span>
                  </div>
                </div>
                <WorkActionSide />
              </div>
            </div>
          </div>
        </div>
      </WorkProvider>
    )
  }
)

WorkVideoCard.displayName = 'WorkVideoCard'
